import { useT } from "@transifex/react";
import { KeyIcon } from "@phosphor-icons/react";
import { PhosphorIcon } from "../../../basic/phosphorIcon/PhosphorIcon";
import type { SchoolClassVisibilities } from "../../../enums/schoolClasses";
import { getClassroomDisplayUrl } from "../../../helpers/schoolClassHelpers";
import { isPrivateSchoolClass } from "../../../utils/schoolClassUtils";
import useBreakpoints from "../../../hooks/useBreakpoints";
import { AreaButton, ButtonColorVariant, ButtonVariant, IconPosition } from "../../teacherArea/AreaButton";

interface ClassAccessSectionProps {
    schoolClassId: string;
    classVisibility: SchoolClassVisibilities;
    hasClassMembers: boolean;
    areClassMembersLoaded: boolean;
    onShareClick: () => void;
}

/**
 * Sharing is the primary action for a private class without students: pupils join it themselves via
 * the class link or QR code. Promoted only once the member load has confirmed the class is empty —
 * while pending or failed it stays secondary, so a class with students never flashes orange.
 */
const ClassAccessSection = (props: ClassAccessSectionProps) => {
    const t = useT();
    const { isAboveSM } = useBreakpoints();

    const isPrivate = isPrivateSchoolClass(props.classVisibility);
    const isInviteState = isPrivate && props.areClassMembersLoaded && !props.hasClassMembers;
    const classDisplayUrl = getClassroomDisplayUrl(props.schoolClassId);

    const t_title = t("Access to the classroom", { _tags: "SchoolClasses, ClassAccessSection" });
    const t_inviteStudents = t("Invite students to your classroom.", {
        _tags: "SchoolClasses, ClassAccessSection",
    });
    const t_privateAccess = t("{classUrl} & personal code", {
        _tags: "SchoolClasses, ClassAccessSection",
        classUrl: classDisplayUrl,
    });
    const t_shareAccessDetails = t("Share access data", { _tags: "SchoolClasses, ClassAccessSection" });

    const getSubtitle = (): string => {
        if (!isPrivate) return classDisplayUrl;
        return isInviteState ? t_inviteStudents : t_privateAccess;
    };

    const renderShareButton = () => (
        <AreaButton
            data-testid="button-share-class-access"
            variant={isInviteState ? ButtonVariant.PRIMARY : ButtonVariant.SECONDARY}
            colorVariant={isInviteState ? ButtonColorVariant.DEFAULT : ButtonColorVariant.DEFAULT_WHITE}
            role="button"
            iconName="share"
            iconPosition={IconPosition.RIGHT}
            onClick={props.onShareClick}
            className={isAboveSM ? "shrink-0" : "w-full"}
        >
            {t_shareAccessDetails}
        </AreaButton>
    );

    const renderSubtitle = () => (
        <p
            className="text-l text-p6-grey-dark break-words"
            data-testid="class-access-subtitle"
        >
            {getSubtitle()}
        </p>
    );

    const renderTitle = () => <h2 className="text-xl font-semibold text-p6-grey-dark">{t_title}</h2>;

    // Desktop keeps title and subtitle stacked next to the icon; mobile gives the subtitle the
    // card's full width below the icon row.
    return (
        <div
            className={`flex bg-white border border-p6-grey-lightest rounded-medium p-m w-full ${
                isAboveSM ? "flex-row items-center gap-s" : "flex-col gap-s"
            }`}
            data-testid="class-access-section"
            data-visibility={props.classVisibility}
            data-invite={isInviteState}
        >
            <div className={`flex gap-s items-center ${isAboveSM ? "flex-1 min-w-0" : ""}`}>
                <PhosphorIcon
                    phosphorIcon={KeyIcon}
                    fontSize="text-xxl"
                    color="text-p6-grey-dark"
                />
                {isAboveSM ? (
                    <div className="flex flex-col gap-xxs min-w-0">
                        {renderTitle()}
                        {renderSubtitle()}
                    </div>
                ) : (
                    renderTitle()
                )}
            </div>
            {!isAboveSM && renderSubtitle()}
            {renderShareButton()}
        </div>
    );
};

export default ClassAccessSection;
